<template>
  <div class="container">
    <PageTitle :back-location="href.home" class="px-4 lg:px-8 pt-4 lg:pt-6">
      {{ $t("site.discover.index.title") }}
    </PageTitle>
    <template v-if="pending">
      <ul class="space-y-8 overflow-hidden exit-container px-4 lg:px-8">
        <li v-for="_ in 3">
          <div class="flex justify-between mb-4">
            <Skeleton class="h-6 w-24" />
            <Skeleton class="h-10 w-10 !rounded-full" />
          </div>
          <ul class="flex flex-nowrap gap-4 px-4 lg:px-0">
            <li v-for="_ in 9">
              <Skeleton class="h-48 lg:h-64 aspect-[5/7] rounded-md" />
              <Skeleton class="h-10 w-full mt-2" />
            </li>
          </ul>
        </li>
      </ul>
    </template>
    <Error v-if="error" :error="error" @retry="refresh" />
    <template v-if="!pending && data">
      <ul class="space-y-8 px-4 lg:px-8 overflow-hidden">
        <li>
          <div class="flex justify-between items-center mb-4">
            <Tooltip fixed text="Based on views">
              <h2 class="text-lg lg:text-xl font-semibold">
                {{ $t("site.discover.index.mostPopularTitle") }}
              </h2>
            </Tooltip>
            <div class="flex items-center">
              <NamiButton
                button-type="secondary"
                pill
                text
                no-waves
                :href="advancedSearchLinks.popular"
                :icon="IconArrowRight"
              />
            </div>
          </div>
          <TitlesCarouselWithHoverExpand
            class="!mb-16"
            :titles="data.allPopularTitles"
            navigation-id="all-popular-titles"
            swiper-wrapper-class="-mx-4 px-4 lg:-mx-8 lg:px-8"
            :breakpoints="{
              0: { slidesPerView: 2, spaceBetween: 8, autoHeight: true },
              360: { slidesPerView: 3, spaceBetween: 8, autoHeight: true },
              640: { slidesPerView: 4, spaceBetween: 8, autoHeight: true },
              768: { slidesPerView: 5, spaceBetween: 8, autoHeight: true },
              1024: { slidesPerView: 6, spaceBetween: 16, autoHeight: true },
              1280: { slidesPerView: 7, spaceBetween: 16, autoHeight: true },
              1536: { slidesPerView: 8, spaceBetween: 16, autoHeight: true },
            }"
            loop
            ref="popularCarousel"
          />
        </li>
        <li>
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-lg lg:text-xl font-semibold">
              {{ $t("site.discover.index.risingStarsTitle") }}
            </h2>
          </div>
          <TitlesCarouselWithHoverExpand
            class="!mb-16"
            :titles="data.risingStarsTitles"
            navigation-id="rising-stars"
            swiper-wrapper-class="-mx-4 px-4 lg:-mx-8 lg:px-8"
            :breakpoints="{
              0: { slidesPerView: 2, spaceBetween: 8, autoHeight: true },
              360: { slidesPerView: 3, spaceBetween: 8, autoHeight: true },
              640: { slidesPerView: 4, spaceBetween: 8, autoHeight: true },
              768: { slidesPerView: 5, spaceBetween: 8, autoHeight: true },
              1024: { slidesPerView: 6, spaceBetween: 16, autoHeight: true },
              1280: { slidesPerView: 7, spaceBetween: 16, autoHeight: true },
              1536: { slidesPerView: 8, spaceBetween: 16, autoHeight: true },
            }"
            loop
          />
        </li>
        <li v-for="[genre, titles] in sortedTitlesByGenre">
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-lg lg:text-xl font-semibold">
              {{ t(`components.tag.${genre}.name`) }}
            </h2>
            <div class="flex items-center">
              <NamiButton
                button-type="secondary"
                pill
                text
                no-waves
                :href="href.discover.tag(genre)"
                :icon="IconArrowRight"
              />
            </div>
          </div>
          <TitlesCarouselWithHoverExpand
            class="!mb-16"
            :titles="titles"
            :navigation-id="genre"
            swiper-wrapper-class="-mx-4 px-4 lg:-mx-8 lg:px-8"
            :breakpoints="{
              0: { slidesPerView: 2, spaceBetween: 8, autoHeight: true },
              360: { slidesPerView: 3, spaceBetween: 8, autoHeight: true },
              640: { slidesPerView: 4, spaceBetween: 8, autoHeight: true },
              768: { slidesPerView: 5, spaceBetween: 8, autoHeight: true },
              1024: { slidesPerView: 6, spaceBetween: 16, autoHeight: true },
              1280: { slidesPerView: 7, spaceBetween: 16, autoHeight: true },
              1536: { slidesPerView: 8, spaceBetween: 16, autoHeight: true },
            }"
            loop
          />
        </li>
        <li>
          <div class="flex justify-between items-center mb-4">
            <Tooltip>
              <h2 class="text-lg lg:text-xl font-semibold">
                {{ $t("site.discover.index.recentlyPublishedTitle") }}
              </h2>
            </Tooltip>
            <div class="flex items-center">
              <NamiButton
                button-type="secondary"
                pill
                text
                no-waves
                :href="advancedSearchLinks.recent"
                :icon="IconArrowRight"
              />
            </div>
          </div>
          <TitlesCarouselWithHoverExpand
            class="!mb-16"
            :titles="data.recentlyPublishedTitles"
            navigation-id="recently-published-titles"
            swiper-wrapper-class="-mx-4 px-4 lg:-mx-8 lg:px-8"
            :breakpoints="{
              0: { slidesPerView: 2, spaceBetween: 8, autoHeight: true },
              360: { slidesPerView: 3, spaceBetween: 8, autoHeight: true },
              640: { slidesPerView: 4, spaceBetween: 8, autoHeight: true },
              768: { slidesPerView: 5, spaceBetween: 8, autoHeight: true },
              1024: { slidesPerView: 6, spaceBetween: 16, autoHeight: true },
              1280: { slidesPerView: 7, spaceBetween: 16, autoHeight: true },
              1536: { slidesPerView: 8, spaceBetween: 16, autoHeight: true },
            }"
          />
        </li>
      </ul>
    </template>
  </div>
</template>

<script setup lang="ts">
import { Title, Tag } from "~/src/api";
import { IconArrowRight } from "@tabler/icons-vue";
import TitlesCarouselWithHoverExpand from "~/components/titles/TitlesCarouselWithHoverExpand.vue";

const nuxtApp = useNuxtApp();
const config = useRuntimeConfig();
const { lg, md } = useBreakpoints();
const { t } = useI18n();
const href = useHref();

const popularCarousel =
  ref<InstanceType<typeof TitlesCarouselWithHoverExpand>>();

const { pending, error, data, refresh } = useAsyncData(
  "discover-titles",
  async () => {
    const tags = await Tag.getTagList();

    const allGenreTagIds = tags.data
      .filter((tag) => tag.attributes.group === "genre")
      .map((tag) => tag.id);

    const [
      popularTitlesPerTag,
      allPopularTitles,
      risingStarsTitles,
      recentlyPublishedTitles,
    ] = await Promise.all([
      getPopularTitlesForTags(allGenreTagIds),
      getPopularTitlesForAllTags(),
      getRisingStarsTitles(),
      getRecentlyPublishedTitles(),
    ]);

    return {
      popularTitlesPerTag,
      allPopularTitles,
      risingStarsTitles,
      recentlyPublishedTitles,
    };
  },
);

async function getPopularTitlesForTags(tags: string[]) {
  const allTitleEntriesPerTag = await Promise.all(
    tags.map(async (tag) => [tag, await getTitlesWithPrimaryTag(tag)] as const),
  );

  return Object.fromEntries(
    allTitleEntriesPerTag.filter(([_, titles]) => titles.length > 5),
  );
}

async function getTitlesWithPrimaryTag(tag?: string) {
  return await Title.fuzzySearch({
    limit: 10,
    includes: ["cover_art"],
    primaryTag: tag,
    order: { views: "desc" },
    contentRatings: ["mature", "restricted", "safe"],
  }).then((r) => r.data);
}

async function getPopularTitlesForAllTags() {
  return await Title.fuzzySearch({
    limit: 10,
    includes: ["cover_art"],
    order: { views: "desc" },
    contentRatings: ["mature", "restricted", "safe"],
  }).then((r) => r.data);
}

async function getRisingStarsTitles() {
  return await Title.fuzzySearch({
    limit: 10,
    includes: ["cover_art"],
    contentRatings: ["mature", "restricted", "safe"],
    order: { "popularity.reactionsWeeklyDeltaNormalized": "desc" },
  }).then((r) => r.data);
}

async function getRecentlyPublishedTitles() {
  return await Title.fuzzySearch({
    limit: 10,
    includes: ["cover_art"],
    order: { publishedAt: "desc" },
    contentRatings: ["mature", "restricted", "safe"],
  }).then((r) => r.data);
}

const advancedSearchLinks = computed(() => {
  function getUrlPathWithSearchParams(url: URL) {
    return url.href.replace(config.public.baseUrl, "");
  }

  function getAdvancedSearchUrl() {
    const url = new URL(config.public.baseUrl);
    url.pathname = href.value.title.search;

    return url;
  }

  function getPopularAdvancedSearchLink() {
    const url = getAdvancedSearchUrl();
    url.searchParams.append("sort", "views.desc");

    return getUrlPathWithSearchParams(url);
  }

  function getRecentlyPublishedAdvancedSearchUrl() {
    const url = getAdvancedSearchUrl();
    url.searchParams.append("sort", "publishedAt.desc");

    return getUrlPathWithSearchParams(url);
  }

  return {
    popular: getPopularAdvancedSearchLink(),
    recent: getRecentlyPublishedAdvancedSearchUrl(),
  };
});

const sortedTitlesByGenre = computed(() => {
  if (!data.value) return [];

  return Object.entries(data.value.popularTitlesPerTag).sort(([a], [b]) => {
    const firstTagName = t(`components.tag.${a}.name`);
    const secondTagName = t(`components.tag.${b}.name`);

    return firstTagName.localeCompare(secondTagName);
  });
});

const { locale, t: translate } = useI18n();

const seo = {
  title: `Discover`,
  description: `Discover titles on NamiComi.`,
  //slug: data.attributes.slug,
  coverUrl: "",
  href: `/en/discover`,
};

nuxtApp.runWithContext(() =>
  useSeoMeta({
    title: seo.title,
    ogTitle: seo.title,
    twitterTitle: seo.title,
    description: seo.description,
    ogDescription: seo.description,
    twitterDescription: seo.description,
    ogUrl: seo.href,
    ogLocale: locale.value,
    ogImage: seo.coverUrl,
    ogImageSecureUrl: seo.coverUrl,
    twitterImage: seo.coverUrl,
    //ogImageHeight: "1200",
    //ogImageWidth: "630",
    ogImageUrl: seo.coverUrl,
    //ogImageType: "image/png",
    ogImageAlt: "NamiComi",
    ogLocaleAlternate: "en_GB",
  }),
);
</script>
